import { useEffect, useState } from "react";
import { useFormik, FormikProvider, FieldArray } from "formik";
import { Modal, Form, Button, InputGroup, Table } from "react-bootstrap";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import CurrencyCustomInput from "../../utils/CurrencyCustomInput";
import ExpensesSelector from "../../utils/ExpensesSelector";
import ItemsTable from "../../utils/ItemsTable";
import { Popover } from "react-tiny-popover";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import TrashCanOutlineIcon from "mdi-react/TrashCanOutlineIcon";
import PlusIcon from "mdi-react/PlusIcon";
import { useBackendUrl } from "../../../utils/hooks";
import Select from "react-select";
import { nanoid } from "nanoid";
import NumberCustomInput from "../../utils/NumberCustomInput";
import currency from "currency.js";
import ConfirmDialog from "../../ConfirmDialogue";

const settlementSourceOptions = [
  { label: "Vendor Account", value: "vendor" },
  { label: "Incentive Account", value: "incentive" },
  { label: "Inventory", value: "inventory" },
];

function SettlePromoIncentiveModal({
  show,
  onClose,
  payout,
  onSuccess,
  refetch,
}) {
  const backendUrl = useBackendUrl();
  const [showJournalPopover, setShowJournalPopover] = useState(false);
  const [showItemsPopover, setShowItemsPopover] = useState(false);
  const [settleAll, setSettleAll] = useState(false);

  const totalAmount = payout?.remainingClaim || 0;

  const formik = useFormik({
    initialValues: {
      AccountID: "",
      Description: "",
      Type: "",
      claimAmount: payout?.remainingClaim || 0,
      handleSettlementBy: "",
      amount: 0,
      valuationPerClaim: 0,
      items: [],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.handleSettlementBy) {
        errors.handleSettlementBy = "Select how to settle";
      }

      if (values.handleSettlementBy === "inventory") {
        if (!values.items || values.items.length === 0) {
          errors.items = "Add at least one item";
        } else {
          const itemErrors = values.items.map((item) => {
            const itemErr = {};
            if (!item.quantity || Number(item.quantity) <= 0)
              itemErr.quantity = "Enter a valid quantity";
            if (!item.amount || Number(item.amount) <= 0)
              itemErr.amount = "Enter a valid amount";
            return itemErr;
          });
          if (itemErrors.some((e) => Object.keys(e).length > 0)) {
            errors.items = itemErrors;
          }
        }
      } else {
        if (!values.AccountID) errors.AccountID = "Select account";
        if (!values.claimAmount || Number(values.claimAmount) <= 0)
          errors.claimAmount = "Enter a valid claim amount";
        if (
          values.valuationPerClaim === "" ||
          values.valuationPerClaim === null ||
          Number(values.valuationPerClaim) <= 0
        )
          errors.valuationPerClaim =
            "Enter a valid monetary valuation per claim";
        if (!values.amount || Number(values.amount) <= 0)
          errors.amount = "Amount must be greater than zero";
      }

      return errors;
    },
    onSubmit: async (values) => {
      const payload = {
        AccountID: values.AccountID,
        AccountName: values.Description,
        claimAmount: settleAll ? totalAmount : Number(values.claimAmount),
        SettleAll: settleAll,
        handleSettlementBy: values.handleSettlementBy,
        payoutId: payout?.id,
        amount: values.amount,
        valuationPerClaim: values.valuationPerClaim,
        items: values.items,
      };
      payload.idempotencyKey = `${payload.payoutId}_${nanoid()}`;

      if (
        await ConfirmDialog({
          title: "Submit",
          description: "Are you sure, you want to submit",
        })
      ) {
        settleIncentiveMutation.mutate(payload);
      }
    },
  });

  console.log(formik.errors);

  const handleSelectedAccount = (account) => {
    formik.setFieldValue("AccountID", account?.AccountID || "");
    formik.setFieldValue("Description", account?.Description || "");
    formik.setFieldValue("Type", account?.Type || "");
    setShowJournalPopover(false);
  };

  // push is supplied by FieldArray render prop
  const handleSelectedItem = (item, push) => {
    push({
      key: nanoid(),
      Bar_Code: item?.Bar_Code,
      name: item?.Item_Name,
      quantity: 0,
      amount: 0,
    });
    setShowItemsPopover(false);
  };

  const settleIncentive = async (payload) => {
    let response = await fetch(
      `${backendUrl}/api/incentive/settlements-promo-claim`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(errorBody.message || "Unable to settle incentive");
    }

    return response.json();
  };

  const settleIncentiveMutation = useMutation(settleIncentive, {
    onSuccess: ({ message }) => {
      toast.success(message || "Incentive settled successfully");
      refetch();
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    },
    onError: (error) => {
      toast.error(
        `Unable to settle incentive: ${error?.message || "Please try again"}`,
      );
    },
  });

  useEffect(() => {
    if (!show) {
      formik.resetForm();
      setShowJournalPopover(false);
      setShowItemsPopover(false);
      setSettleAll(false);
    }
  }, [show]);

  // Whenever the effective claim amount (respecting settleAll) or the
  // per-claim valuation changes, recompute the total settlement amount.
  const effectiveClaimAmount = settleAll
    ? totalAmount
    : formik.values.claimAmount;

  useEffect(() => {
    if (formik.values.handleSettlementBy === "inventory") return;

    const computed = currency(effectiveClaimAmount || 0).multiply(
      formik.values.valuationPerClaim || 0,
    ).value;

    if (computed !== formik.values.amount) {
      formik.setFieldValue("amount", computed);
    }
  }, [
    effectiveClaimAmount,
    formik.values.valuationPerClaim,
    formik.values.handleSettlementBy,
  ]);

  // When settling via inventory, sum item quantity * amount into "amount"
  useEffect(() => {
    if (formik.values.handleSettlementBy !== "inventory") return;

    const computed = (formik.values.items || []).reduce(
      (sum, item) =>
        currency(sum).add(
          currency(item.quantity || 0).multiply(item.amount || 0),
        ).value,
      0,
    );

    if (computed !== formik.values.amount) {
      formik.setFieldValue("amount", computed);
    }
  }, [formik.values.items, formik.values.handleSettlementBy]);

  const isInventory = formik.values.handleSettlementBy === "inventory";

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size={isInventory ? "lg" : "md"}
      animation={false}
      autoFocus={false}
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {settleAll ? "Settle Full Claim" : "Settle Incentive Claim"}
        </Modal.Title>
      </Modal.Header>

      <FormikProvider value={formik}>
        <Modal.Body>
          <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
            <div className="row">
              <div className="mb-3 ">
                <Form.Label>Total Claim</Form.Label>
                <Form.Control
                  readOnly
                  value={totalAmount}
                  className="bg-lighter"
                />
              </div>
            </div>

            <div className="row">
              <Form.Group className="mb-3 ">
                <Form.Label>Settle By</Form.Label>
                <Select
                  options={settlementSourceOptions}
                  value={settlementSourceOptions.find(
                    (el) => el.value === formik.values.handleSettlementBy,
                  )}
                  onChange={(selected) =>
                    formik.setFieldValue("handleSettlementBy", selected?.value)
                  }
                />
                {formik.touched.handleSettlementBy &&
                formik.errors.handleSettlementBy ? (
                  <div className="invalid-feedback d-block">
                    {formik.errors.handleSettlementBy}
                  </div>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3 ">
                <Form.Label>Corresponding Account</Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    placeholder="Select Corresponding Account"
                    value={
                      formik.values.AccountID
                        ? `${formik.values.AccountID} / ${
                            formik.values.Description
                          } / ${formik.values.Type}`
                        : ""
                    }
                    onChange={() => {}}
                    readOnly
                    isInvalid={
                      formik.touched.AccountID && !!formik.errors.AccountID
                    }
                  />
                  <Popover
                    isOpen={showJournalPopover}
                    onClickOutside={() => setShowJournalPopover(false)}
                    content={() => (
                      <ExpensesSelector
                        handleSelectedExpense={handleSelectedAccount}
                        usage="chart of accounts"
                        onHide={() => setShowJournalPopover(false)}
                      />
                    )}
                    position="bottom"
                  >
                    <InputGroup.Text
                      onClick={() => setShowJournalPopover(true)}
                    >
                      <MagnifyIcon />
                    </InputGroup.Text>
                  </Popover>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.AccountID}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </div>

            {!isInventory && (
              <div className="row">
                <Form.Group className="mb-3 ">
                  <Form.Label>Claim</Form.Label>

                  <NumberCustomInput
                    name="claimAmount"
                    value={settleAll ? totalAmount : formik.values.claimAmount}
                    disabled={settleAll}
                    onValueChange={(value, name) => {
                      if (value !== undefined && value <= totalAmount) {
                        formik.setFieldValue(name, value);
                      }
                    }}
                    onBlur={() => formik.setFieldTouched("claimAmount", true)}
                  />
                  {formik.touched.claimAmount && formik.errors.claimAmount ? (
                    <div className="invalid-feedback d-block">
                      {formik.errors.claimAmount}
                    </div>
                  ) : null}
                </Form.Group>
                <Form.Group className="mb-3 ">
                  <Form.Label>Monetary Valuation (Per Claim)</Form.Label>
                  <CurrencyCustomInput
                    name="valuationPerClaim"
                    value={formik.values.valuationPerClaim}
                    onValueChange={(value, name) => {
                      formik.setFieldValue(name, value);
                    }}
                    onBlur={() =>
                      formik.setFieldTouched("valuationPerClaim", true)
                    }
                    isInvalid={
                      formik.touched.valuationPerClaim &&
                      !!formik.errors.valuationPerClaim
                    }
                  />
                  {formik.touched.valuationPerClaim &&
                  formik.errors.valuationPerClaim ? (
                    <div className="invalid-feedback d-block">
                      {formik.errors.valuationPerClaim}
                    </div>
                  ) : null}
                </Form.Group>
              </div>
            )}

            {isInventory && (
              <FieldArray name="items">
                {({ push, remove }) => (
                  <div className="mb-3">
                    {typeof formik.errors.items === "string" ? (
                      <div className="invalid-feedback d-block mb-2">
                        {formik.errors.items}
                      </div>
                    ) : null}

                    {formik.values.items.length > 0 && (
                      <Table bordered size="sm" responsive>
                        <thead>
                          <tr>
                            <th>Barcode</th>
                            <th>Item Name</th>
                            <th style={{ width: 100 }}>Quantity</th>
                            <th style={{ width: 120 }}>Amount</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {formik.values.items.map((item, index) => {
                            const itemError =
                              Array.isArray(formik.errors.items) &&
                              formik.errors.items[index]
                                ? formik.errors.items[index]
                                : {};

                            return (
                              <tr key={item.key || index}>
                                <td className="align-middle">
                                  {item?.Bar_Code}
                                </td>
                                <td className="align-middle">{item.name}</td>
                                <td>
                                  <NumberCustomInput
                                    style={{ width: 100 }}
                                    name={`items.${index}.quantity`}
                                    value={item.quantity}
                                    onValueChange={(value, name) =>
                                      formik.setFieldValue(name, value)
                                    }
                                    isInvalid={!!itemError.quantity}
                                  />
                                </td>
                                <td>
                                  <CurrencyCustomInput
                                    style={{ width: 120 }}
                                    name={`items.${index}.amount`}
                                    value={item.amount}
                                    onValueChange={(value, name) =>
                                      formik.setFieldValue(name, value)
                                    }
                                    isInvalid={!!itemError.amount}
                                  />
                                </td>
                                <td className="text-center align-middle">
                                  <TrashCanOutlineIcon
                                    size={18}
                                    className="text-danger cursor-pointer"
                                    onClick={() => remove(index)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0" />
                      <Popover
                        isOpen={showItemsPopover}
                        onClickOutside={() => setShowItemsPopover(false)}
                        content={() => (
                          <ItemsTable
                            handleSelectedItem={(item) =>
                              handleSelectedItem(item, push)
                            }
                          />
                        )}
                        position="bottom"
                      >
                        <Button
                          type="button"
                          variant="outline-primary"
                          className="text-nowrap"
                          size="sm"
                          onClick={() => {
                            console.log(
                              "this is the total clams: ",
                              totalAmount,
                              formik.values.items.length,
                            );
                            if (
                              formik.values.items?.length >= Number(totalAmount)
                            ) {
                              toast.warn(
                                "You can not add more items than your total claims",
                              );
                              return;
                            }
                            setShowItemsPopover(!showItemsPopover);
                          }}
                        >
                          <PlusIcon size={16} className="me-1" />
                          Add Item
                        </Button>
                      </Popover>
                    </div>
                  </div>
                )}
              </FieldArray>
            )}

            <div className="row">
              <Form.Group className="mb-3 ">
                <Form.Label>Amount</Form.Label>
                <CurrencyCustomInput
                  name="amount"
                  value={formik.values.amount}
                  disabled
                  readOnly
                  onValueChange={() => {}}
                />
                {formik.touched.amount && formik.errors.amount ? (
                  <div className="invalid-feedback d-block">
                    {formik.errors.amount}
                  </div>
                ) : null}
              </Form.Group>
            </div>

            <Form.Group className="mb-3 ">
              <Form.Check
                type="switch"
                id="settleAllSwitch"
                label="Settle Full Payout Claim"
                checked={settleAll}
                onChange={(e) => setSettleAll(e.target.checked)}
              />
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end">
              <Button
                variant="white"
                type="button"
                onClick={onClose}
                disabled={settleIncentiveMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={settleIncentiveMutation.isLoading}
              >
                {settleIncentiveMutation.isLoading
                  ? "Processing..."
                  : settleAll
                  ? "Settle Full Claim"
                  : "Settle Claim"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </FormikProvider>
    </Modal>
  );
}

export default SettlePromoIncentiveModal;
